/**
 * Recording helpers for PlaywrightClient.
 *
 * @module playwright-client/recording
 */
import * as fs from 'fs/promises';
import * as path from 'path';
import type { BrowserContext } from 'playwright';

import type { BrowserLogger } from '../logger.js';

export type TraceStopResult = {
  trace: string;
  traces: string[];
  network: string;
  resources: string;
  directory: string | null;
  legend: string | null;
};

const MAX_TRACE_GROUP_CLOSES = 32;

type TraceRecorderOptions = {
  tmpBaseDir: string;
  logger: BrowserLogger;
  getContext: () => Promise<BrowserContext>;
  getCurrentContext: () => BrowserContext | null;
  now?: () => number;
};

export class PlaywrightTraceRecorder {
  private tracingActive = false;
  private traceDir: string | null = null;
  private readonly traceChunks: string[] = [];
  private readonly allTraceDirs = new Set<string>();
  private readonly tmpBaseDir: string;
  private readonly logger: BrowserLogger;
  private readonly getContext: () => Promise<BrowserContext>;
  private readonly getCurrentContext: () => BrowserContext | null;
  private readonly now: () => number;
  private traceGroupDepthValue = 0;

  constructor(options: TraceRecorderOptions) {
    this.tmpBaseDir = options.tmpBaseDir;
    this.logger = options.logger;
    this.getContext = options.getContext;
    this.getCurrentContext = options.getCurrentContext;
    this.now = options.now ?? Date.now;
  }

  isActive(): boolean {
    return this.tracingActive;
  }

  get chunkCount(): number {
    return this.traceChunks.length;
  }

  async start(): Promise<void> {
    this.traceChunks.length = 0;
    this.allTraceDirs.clear();
    await this.beginOnContext();
  }

  /**
   * Start a Playwright trace on the current context. Used both for the initial
   * start and to resume tracing after a context swap. State is updated only
   * after `tracing.start()` resolves.
   */
  async beginOnContext(): Promise<void> {
    const context = await this.getContext();
    const traceDir = path.join(
      this.tmpBaseDir,
      `playwright-trace-${this.now()}-${this.traceChunks.length}`
    );
    await fs.mkdir(traceDir, { recursive: true });
    try {
      await context.tracing.start({ screenshots: true, snapshots: true });
    } catch (err) {
      await fs.rm(traceDir, { recursive: true, force: true }).catch(() => {});
      throw err;
    }
    this.traceDir = traceDir;
    this.allTraceDirs.add(traceDir);
    this.tracingActive = true;
    this.traceGroupDepthValue = 0;
  }

  /**
   * Flush the active context-bound trace to a chunk path. Errors are
   * best-effort and leave existing chunks intact so context swaps can continue.
   */
  async flushCurrentChunk(): Promise<string | null> {
    const context = this.getCurrentContext();
    if (!this.tracingActive || !context || !this.traceDir) return null;
    const chunkPath = path.join(this.traceDir, 'trace.zip');
    try {
      await context.tracing.stop({ path: chunkPath });
      this.traceChunks.push(chunkPath);
      return chunkPath;
    } catch (err) {
      this.logger.warn('[DirectPlaywright] Failed to flush trace chunk', {
        error: err instanceof Error ? err.message : String(err),
      });
      return null;
    } finally {
      this.tracingActive = false;
      this.traceGroupDepthValue = 0;
    }
  }

  markInactive(): void {
    this.tracingActive = false;
    this.traceGroupDepthValue = 0;
  }

  traceGroupDepth(): number {
    return this.traceGroupDepthValue;
  }

  async traceGroup(name: string): Promise<void> {
    if (!this.tracingActive) return;
    const context = await this.getContext();
    await context.tracing.group(name);
    this.traceGroupDepthValue++;
  }

  async traceGroupEnd(): Promise<void> {
    if (!this.tracingActive || this.traceGroupDepthValue <= 0) return;
    const context = await this.getContext();
    await context.tracing.groupEnd();
    this.traceGroupDepthValue = Math.max(0, this.traceGroupDepthValue - 1);
  }

  async traceGroupEndUntil(depth: number): Promise<void> {
    const targetDepth = Math.max(0, depth);
    let closeCount = 0;
    while (this.traceGroupDepthValue > targetDepth && closeCount < MAX_TRACE_GROUP_CLOSES) {
      await this.traceGroupEnd();
      closeCount++;
    }
  }

  async stop(): Promise<TraceStopResult> {
    await this.flushCurrentChunk();

    if (this.traceChunks.length === 0) {
      throw new Error('No trace chunks captured (tracing not started, or all flushes failed)');
    }

    const chunks = [...this.traceChunks];
    if (chunks.length > 1) {
      this.logger.info('[DirectPlaywright] Captured multiple trace chunks across context swaps', {
        chunkCount: chunks.length,
      });
    }

    return {
      trace: chunks[0]!,
      traces: chunks,
      network: '',
      resources: '',
      directory: path.dirname(chunks[0]!),
      legend: `Trace saved to ${chunks[0]!}`,
    };
  }

  async resumeAfterRecreatedContext(): Promise<void> {
    if (!this.tracingActive) return;
    this.tracingActive = false;
    await this.beginOnContext().catch((err) => {
      this.logger.warn('[DirectPlaywright] Failed to resume tracing after browser recovery', {
        error: err instanceof Error ? err.message : String(err),
        chunksCapturedSoFar: this.traceChunks.length,
      });
    });
  }

  async cleanupTraceDirectories(): Promise<void> {
    const traceDirsToClean = new Set<string>(this.allTraceDirs);
    if (this.traceDir) traceDirsToClean.add(this.traceDir);
    await Promise.allSettled(
      [...traceDirsToClean].map((dir) =>
        fs.rm(dir, { recursive: true, force: true }).catch((err) =>
          this.logger.warn('[DirectPlaywright] Failed to clean up trace directory', {
            traceDir: dir,
            error: err instanceof Error ? err.message : String(err),
          })
        )
      )
    );
    this.traceDir = null;
    this.traceChunks.length = 0;
    this.allTraceDirs.clear();
    this.tracingActive = false;
    this.traceGroupDepthValue = 0;
  }
}
